import React from "react";
import { motion } from "framer-motion";
import { FEATURES } from "@/constants/plantusTestIds";
import {
  FileArrowDown,
  MapTrifold,
  CloudSlash,
  Lightning,
  Leaf,
  ShieldStar,
  ClockCounterClockwise,
} from "@phosphor-icons/react";

const FARMER_IMG = "/screenshots/founder_field.jpg";
const FIELD_PATTERN =
  "https://images.unsplash.com/photo-1516822277566-bb38424a2b77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxhZXJpYWwlMjBhZ3JpY3VsdHVyZSUyMGZpZWxkJTIwcGF0dGVybnxlbnwwfHx8fDE3ODAwMzM3NjF8MA&ixlib=rb-4.1.0&q=85";

const CardShell = ({ children, className = "", testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: "easeOut" }}
    data-testid={testId}
    className={`relative overflow-hidden rounded-3xl shadow-[0_1px_2px_rgba(20,30,20,0.04),0_6px_24px_-12px_rgba(20,30,20,0.12)] ${
      /bg-/.test(className) ? "" : "bg-white"
    } ${/border-(?!\[)/.test(className) ? "" : "border border-bone-200"} ${className}`}
  >
    {children}
  </motion.div>
);

export const Features = () => {
  return (
    <section
      id="features"
      data-testid={FEATURES.section}
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta mb-3">
            Funktionen
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest-900 tracking-tight">
            Alles, was Sie für{" "}
            <span className="text-forest-600">lückenlose Dokumentation</span> brauchen.
          </h2>
          <p className="mt-5 font-body text-base sm:text-lg text-[#4A524C]">
            Plantus ist kein generisches Notizbuch. Jede Funktion ist darauf
            ausgelegt, Ihren Arbeitsalltag zu erleichtern, und gleichzeitig
            EU&nbsp;2023/564 und § 11 PflSchG zu erfüllen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 lg:gap-6">
          {/* Large card, Behördenexport */}
          <CardShell
            testId={FEATURES.card("export")}
            className="col-span-1 md:col-span-8 lg:col-span-8 p-8 lg:p-10 min-h-[300px] lg:min-h-[340px]"
          >
            <div
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage: `url(${FIELD_PATTERN})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div className="relative flex flex-col h-full">
              <div className="w-12 h-12 rounded-2xl bg-forest-50 border border-forest-100 flex items-center justify-center mb-5">
                <FileArrowDown size={24} weight="duotone" className="text-forest-700" />
              </div>
              <h3 className="font-heading text-2xl lg:text-3xl font-medium text-forest-900 max-w-md">
                Kontrollbereit auf Knopfdruck
              </h3>
              <p className="mt-3 font-body text-[15px] lg:text-base text-[#4A524C] max-w-md">
                Alle Anwendungen sauber dokumentiert und jederzeit exportierbar.
                Erstellen Sie mit einem Klick vollständige <strong>PDF</strong>
                {" "}und <strong>JSON</strong>-Dateien für Dokumentation, Nachweise
                und Kontrollen, exakt nach den Vorgaben der Pflanzenschutzämter.
              </p>
              <div className="mt-auto pt-6 flex items-center gap-2 text-sm font-semibold text-terracotta">
                PDF · JSON · 2027-ready
              </div>
            </div>
          </CardShell>

          {/* GPS */}
          <CardShell
            testId={FEATURES.card("gps")}
            className="col-span-1 md:col-span-4 lg:col-span-4 p-8 min-h-[300px] lg:min-h-[340px] bg-forest-700 text-white border-forest-700"
          >
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center mb-5">
              <MapTrifold size={24} weight="duotone" className="text-white" />
            </div>
            <h3 className="font-heading text-2xl font-medium">GPS-Tracking</h3>
            <p className="mt-3 font-body text-[15px] text-white/75">
              Automatische Erkennung der Feldgrenzen mit Sub-Meter-Präzision.
              Keine manuellen Skizzen mehr.
            </p>
            <svg
              className="absolute -bottom-6 -right-6 w-44 h-44 opacity-20"
              viewBox="0 0 100 100"
              fill="none"
            >
              <circle cx="50" cy="50" r="48" stroke="white" strokeWidth="0.6" />
              <circle cx="50" cy="50" r="34" stroke="white" strokeWidth="0.6" />
              <circle cx="50" cy="50" r="20" stroke="white" strokeWidth="0.6" />
              <circle cx="50" cy="50" r="3" fill="#D97743" />
            </svg>
          </CardShell>

          {/* Offline */}
          <CardShell
            testId={FEATURES.card("offline")}
            className="col-span-1 md:col-span-4 lg:col-span-4 p-8 min-h-[260px]"
          >
            <div className="w-12 h-12 rounded-2xl bg-terracotta/10 border border-terracotta/20 flex items-center justify-center mb-5">
              <CloudSlash size={24} weight="duotone" className="text-terracotta" />
            </div>
            <h3 className="font-heading text-2xl font-medium text-forest-900">Offline-Modus</h3>
            <p className="mt-3 font-body text-[15px] text-[#4A524C]">
              Kein Netz auf dem Feld? Kein Problem. Daten werden automatisch
              synchronisiert, sobald Sie wieder Empfang haben.
            </p>
          </CardShell>

          {/* Praxis - large */}
          <CardShell
            testId={FEATURES.card("practice")}
            className="col-span-1 md:col-span-8 lg:col-span-8 min-h-[280px] overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-5 h-full">
              <div className="sm:col-span-3 p-8 lg:p-10 flex flex-col">
                <div className="w-12 h-12 rounded-2xl bg-terracotta/10 border border-terracotta/20 flex items-center justify-center mb-5">
                  <Lightning size={24} weight="duotone" className="text-terracotta" />
                </div>
                <h3 className="font-heading text-2xl font-medium text-forest-900">
                  In Sekunden erfasst
                </h3>
                <p className="mt-3 font-body text-[15px] text-[#4A524C] max-w-md">
                  Wenige Klicks, automatische Echtzeit-Aufzeichnung von Datum,
                  GPS-Position und Wetter. Eine Anwendung ist direkt vor Ort
                  dokumentiert, einfacher geht es nicht.
                </p>
                <div className="mt-auto pt-6 flex items-center gap-2 text-sm font-semibold text-terracotta">
                  ⌀ &lt; 30 Sekunden pro Anwendung
                </div>
              </div>
              <div
                className="sm:col-span-2 min-h-[180px] sm:min-h-full"
                style={{
                  backgroundImage: `url(${FARMER_IMG})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            </div>
          </CardShell>

          {/* Wirkstoff-Datenbank */}
          <CardShell
            testId={FEATURES.card("database")}
            className="col-span-1 md:col-span-4 lg:col-span-4 p-8 min-h-[220px]"
          >
            <div className="w-12 h-12 rounded-2xl bg-forest-50 border border-forest-100 flex items-center justify-center mb-5">
              <Leaf size={24} weight="duotone" className="text-forest-700" />
            </div>
            <h3 className="font-heading text-2xl font-medium text-forest-900">
              BVL-Wirkstoff-Datenbank
            </h3>
            <p className="mt-3 font-body text-[15px] text-[#4A524C] max-w-md">
              Zugelassene Pflanzenschutzmittel sind direkt in der App
              hinterlegt, inklusive Wartezeiten und Auflagen.
            </p>
          </CardShell>

          {/* Nachträglich digitalisieren */}
          <CardShell
            testId={FEATURES.card("retro")}
            className="col-span-1 md:col-span-4 lg:col-span-4 p-8 min-h-[220px]"
          >
            <div className="w-12 h-12 rounded-2xl bg-forest-50 border border-forest-100 flex items-center justify-center mb-5">
              <ClockCounterClockwise size={24} weight="duotone" className="text-forest-700" />
            </div>
            <h3 className="font-heading text-2xl font-medium text-forest-900">
              Nachträglich digitalisierbar
            </h3>
            <p className="mt-3 font-body text-[15px] text-[#4A524C] max-w-md">
              Bestehende Papier-Aufzeichnungen lassen sich jederzeit einfach
              in der App nacherfassen, mit Datum, Uhrzeit und Standort frei
              wählbar.
            </p>
          </CardShell>

          {/* Compliance */}
          <CardShell
            testId={FEATURES.card("compliance")}
            className="col-span-1 md:col-span-4 lg:col-span-4 p-8 min-h-[220px]"
          >
            <div className="w-12 h-12 rounded-2xl bg-terracotta/10 border border-terracotta/20 flex items-center justify-center mb-5">
              <ShieldStar size={24} weight="duotone" className="text-terracotta" />
            </div>
            <h3 className="font-heading text-2xl font-medium text-forest-900">
              Audit-sicher archiviert
            </h3>
            <p className="mt-3 font-body text-[15px] text-[#4A524C] max-w-md">
              Lückenlose Aufbewahrung über alle gesetzlich vorgeschriebenen
              Fristen hinweg, revisionssicher und jederzeit abrufbar.
            </p>
          </CardShell>
        </div>
      </div>
    </section>
  );
};
